export interface DraggedItem {
  kind: string;
  type: string;
}

export interface DropVerdict {
  accepted: boolean;
  text: string;
  variant: 'info' | 'danger';
}

interface Params {
  items: Array<DraggedItem>;
  overlayText: string;
  disabled?: boolean;
  allowedItemKind?: 'file' | string;
  allowedItemTypes?: Array<string>;
}

/** Matches a MIME type against one allowed entry, which may be a wildcard such as `image/*`. */
const matchesType = (allowed: string, actual: string): boolean =>
  allowed.endsWith('/*') ? actual.startsWith(allowed.slice(0, -1)) : allowed === actual;

/**
 * Decides whether a native file drag may be dropped, and what the overlay should say.
 *
 * Browsers only expose `kind`/`type` on the dragged items, never the file names, and Safari and
 * Firefox sometimes expose nothing at all before the drop. An empty item list therefore counts as
 * acceptable — the same fallback the react-dnd implementation used.
 */
export const evaluateDrop = ({
  items,
  overlayText,
  disabled = false,
  allowedItemKind,
  allowedItemTypes,
}: Params): DropVerdict => {
  const neutral: DropVerdict = { accepted: true, text: overlayText, variant: 'info' };

  if (disabled) return { ...neutral, accepted: false };
  if (items.length < 1) return neutral;

  const [firstItem] = items;

  if (allowedItemKind != null && allowedItemKind !== firstItem.kind) {
    return { accepted: false, text: 'Invalid File Kind!', variant: 'danger' };
  }

  // An empty type means the browser will not say what this is, not that it is the wrong thing.
  const typeIsKnown = firstItem.type !== '';
  if (
    typeIsKnown &&
    allowedItemTypes != null &&
    !allowedItemTypes.some((t) => matchesType(t, firstItem.type))
  ) {
    return { accepted: false, text: 'Invalid File Type!', variant: 'danger' };
  }

  return neutral;
};
